// Recetas digitales. Centro Medico → Clínica → Recetas.
// Emite recetas firmadas (snapshot de la firma del médico) con código de
// verificación (QR), renovables para crónicos. Impresión con window.print().
import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Download, FileSignature, Loader2, Plus, Printer, RefreshCw } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api/client';
import { useAuth } from '@/lib/hooks';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { RecetaEditor } from './RecetaEditor';
import {
  Modal, PacienteRow, RecetaDetalle, RecetaRow, cantidadEnLetras, fieldCls, medRpc,
} from './shared';
import { EncabezadoImpreso, FirmaImpreso, edadTexto, fechaPais } from './impreso';
import type { Diagnostico } from './shared';
import { LOCALE } from '@/lib/utils/regional';

export function Recetas() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [pacienteId, setPacienteId] = useState('');
  const { data: pacientes = [] } = useQuery<PacienteRow[]>({ queryKey: ['med-pacientes', ''], queryFn: () => medRpc<PacienteRow[]>('med_pacientes', { _busqueda: '' }) });
  const { data: recetas = [], isFetching } = useQuery<RecetaRow[]>({
    queryKey: ['med-recetas', pacienteId], queryFn: () => medRpc<RecetaRow[]>('med_recetas', { _paciente_id: pacienteId }), enabled: !!pacienteId,
  });

  const [nueva, setNueva] = useState(false);
  const [imprimir, setImprimir] = useState<string | null>(null);
  const refrescar = () => void qc.invalidateQueries({ queryKey: ['med-recetas', pacienteId] });

  const anular = useMutation({
    mutationFn: (id: string) => medRpc('med_anular_receta', { _id: id, _motivo: 'Anulada por el médico' }),
    onSuccess: () => { toast.success('Receta anulada'); refrescar(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const renovar = useMutation({
    mutationFn: (id: string) => medRpc('med_renovar_receta', { _id: id }),
    onSuccess: () => { toast.success('Receta renovada'); refrescar(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Recetas</h1>
        <div className="flex items-center gap-2">
          <select className={`${fieldCls} w-72`} value={pacienteId} onChange={(e) => setPacienteId(e.target.value)}>
            <option value="">— Selecciona un paciente —</option>
            {pacientes.map((p) => <option key={p.id} value={p.id}>{p.razon_social} ({p.identificacion ?? 's/i'})</option>)}
          </select>
          {pacienteId && <Button onClick={() => setNueva(true)}><Plus className="w-4 h-4 mr-1" /> Nueva receta</Button>}
        </div>
      </div>

      {!pacienteId && <Card><CardContent className="py-12 text-center text-muted-foreground">Selecciona un paciente para ver sus recetas.</CardContent></Card>}
      {pacienteId && (
        <Card>
          <CardContent className="overflow-x-auto pt-4">
            {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Médico</th>
                <th className="py-2 pr-3">Dx</th><th className="py-2 pr-3">Ítems</th><th className="py-2 pr-3">Tipo</th>
                <th className="py-2 pr-3">Estado</th><th className="py-2"></th>
              </tr></thead>
              <tbody>
                {recetas.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin recetas</td></tr>}
                {recetas.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3 font-mono font-medium">{r.codigo}</td>
                    <td className="py-2 pr-3">{new Date(r.fecha).toLocaleDateString(LOCALE)}</td>
                    <td className="py-2 pr-3">{r.medico}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{r.diagnostico || '—'}</td>
                    <td className="py-2 pr-3">{r.items}</td>
                    <td className="py-2 pr-3">{r.tipo === 'cronica' ? `Crónica (${r.refills_usados}/${r.refills_total})` : 'Normal'}</td>
                    <td className="py-2 pr-3">{r.estado === 'anulada' ? <span className="text-urgente">Anulada</span> : 'Emitida'}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Imprimir" aria-label="Imprimir" onClick={() => setImprimir(r.id)}><Printer className="w-4 h-4" /></button>
                      {r.estado === 'emitida' && r.renovable && r.refills_usados < r.refills_total && (
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-primary hover:bg-blue-50 ml-1" title="Renovar" aria-label="Renovar" onClick={() => renovar.mutate(r.id)}><RefreshCw className="w-4 h-4" /></button>
                      )}
                      {r.estado === 'emitida' && (
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 ml-1" title="Anular" aria-label="Anular" onClick={async () => { if (!(await dlg.confirmar(`¿Anular la receta ${r.codigo}?`, { peligro: true }))) return; anular.mutate(r.id); }}><Ban className="w-4 h-4" /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {nueva && pacienteId && <NuevaReceta pacienteId={pacienteId} onClose={() => setNueva(false)} onSaved={() => { setNueva(false); refrescar(); }} />}
      {imprimir && <RecetaPrint id={imprimir} onClose={() => setImprimir(null)} />}
    </div>
  );
}

// ---- Nueva receta ----
// Editor ÚNICO compartido con la ficha del paciente (RecetaEditor): aquí solo
// se envuelve en un modal. Cualquier cambio al formulario va en RecetaEditor.tsx.
export function NuevaReceta({ pacienteId, onClose, onSaved, diagnosticoInicial, dxInicial }: { pacienteId: string; onClose: () => void; onSaved: () => void; diagnosticoInicial?: string; dxInicial?: Diagnostico[] }) {
  return (
    <Modal title="Nueva receta" onClose={onClose} wide>
      <RecetaEditor pacienteId={pacienteId} diagnosticoInicial={diagnosticoInicial} dxInicial={dxInicial} onCancel={onClose} onSaved={onSaved} />
    </Modal>
  );
}

// ---- Impresión de receta ----
// Formato "dos paneles": la hoja se corta/dobla al medio — izquierda para la
// FARMACIA (prescripción técnica), derecha para el PACIENTE (indicaciones en
// lenguaje llano). Cada columna repite el mismo encabezado.

// Primera letra en mayúscula (vía, forma) sin tocar el resto del texto.
function cap(s: string | null | undefined): string {
  const t = (s ?? '').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
}

// cantidadEnLetras() devuelve "9 (nueve)"; aquí capitalizamos la palabra entre paréntesis: "9 (Nueve)".
function cantidadConLetraMayus(cantidad: string): string {
  return cantidadEnLetras(cantidad).replace(/\((\w)/, (_m, c: string) => `(${c.toUpperCase()}`);
}

// Estado de la firma electrónica de la receta (RPC med_receta_firma_estado, script 687)
interface FirmaEstado {
  estado: 'pendiente' | 'generando' | 'firmada' | 'error' | null;
  firmada_at: string | null; firmante: string | null; error: string | null;
  tiene_pdf: boolean; puede_firmar: boolean;
}

function CopiaReceta({ data, logo, firmaManual, firma }: { data: RecetaDetalle; logo?: string | null; firmaManual: boolean; firma?: FirmaEstado | null }) {
  const r = data.receta;
  const recetaNo = (r.codigo || '').replace(/\D/g, '').slice(-6) || r.codigo;
  const codNo = (r.codigo_verificacion || '').replace(/[^0-9a-zA-Z]/g, '').slice(0, 7).toUpperCase();
  const pacienteNombre = `${(data.paciente || '').toUpperCase()}${r.estado === 'anulada' ? ' (ANULADA)' : ''}`;
  const edadStr = edadTexto(data.paciente_edad, data.paciente_edad_meses);
  const alergiasTexto = (r.datos?.alergias_farmacologicas || '').trim() || 'No refiere alergias farmacológicas';
  const dxTexto = r.datos?.diagnosticos?.length
    ? r.datos.diagnosticos.map((d) => `${d.cie10_codigo ?? ''} ${d.descripcion}`.trim()).join(' ')
    : (r.diagnostico || '—');
  const signosTexto = (r.datos?.signos_alarma ?? []).filter(Boolean).join(', ') || 'No signos de alarma';
  const recomendTexto = r.datos?.recomendaciones || r.indicaciones || '—';
  const proximaTexto = r.datos?.proxima_cita ? fechaPais(`${r.datos.proxima_cita}T00:00:00`, data.empresa_pais) : '';

  // Encabezado repetido en ambas columnas (la hoja se corta/dobla al medio).
  // La cabecera es la misma para todo impreso médico y en cualquier país: lo
  // que cambia son las etiquetas (CI/DNI/CC) y el formato de fecha.
  const renderHeader = () => (
    <EncabezadoImpreso
      cab={data} logo={logo} fecha={r.fecha}
      derecha={<><b>Receta #</b> {recetaNo} · <b>Cod#</b> {codNo}</>}
      paciente={pacienteNombre}
      identificacion={data.paciente_ident}
      edad={edadStr}
    />
  );

  const renderSeccion = (titulo: string, contenido: ReactNode) => (
    <div className="mb-1.5 text-[11px]">
      <p className="font-bold">{titulo}</p>
      {contenido}
    </div>
  );

  // Farmacia: línea técnica por medicamento. Ej: "1. IBUPROFENO (BUPREX FLASH) 400MG, via Oral, Cápsula(s) # 9 (Nueve)."
  const renderPrescripcion = () => (
    <>
      {data.items.map((it, i) => {
        let base = it.medicamento;
        if (it.comercial) base += ` (${it.comercial})`;
        if (it.concentracion) base += ` ${it.concentracion}`;
        const segmentos: string[] = [];
        if (it.via) segmentos.push(`via ${cap(it.via)}`);
        if (it.cantidad) segmentos.push(`${cap(it.forma)}(s) # ${cantidadConLetraMayus(it.cantidad)}`);
        return <p key={it.id}>{i + 1}. {base}{segmentos.map((s) => `, ${s}`).join('')}.</p>;
      })}
    </>
  );

  // Paciente: mismo medicamento + indicaciones en lenguaje llano, en MAYÚSCULAS.
  const renderIndicaciones = () => (
    <>
      {data.items.map((it, i) => {
        let base = it.medicamento;
        if (it.comercial) base += ` (${it.comercial})`;
        if (it.concentracion) base += ` ${it.concentracion}`;
        if (it.via) base += `, via ${cap(it.via)}`;
        const indicaciones = (it.indicaciones || [it.dosis, it.frecuencia, it.duracion_dias ? `${it.duracion_dias} días` : null].filter(Boolean).join(' · ')).toUpperCase();
        return <p key={it.id}>{i + 1}. {base}. {indicaciones}</p>;
      })}
    </>
  );

  // Firma del médico, repetida en ambas columnas:
  // · MANUAL → imagen de la rúbrica (o espacio en blanco para firmar a mano).
  // · ELECTRÓNICA (.p12) → QR de verificación pública (/rx/<código>) + leyenda;
  //   el documento con valor legal es el PDF firmado que genera el sistema.
  const urlVerificacion = `${window.location.origin}/rx/${r.codigo_verificacion}`;
  const renderFirma = () => (
    <FirmaImpreso
      pais={data.empresa_pais}
      medico={data.medico}
      licencia={data.medico_licencia}
      especialidad={data.especialidad}
      marca={firmaManual
        ? (data.medico_firma
            ? <img src={data.medico_firma} alt="rúbrica del médico" className="mx-auto h-9 object-contain" />
            : <div className="h-9" />)
        : (
          <div className="mx-auto flex items-center justify-center gap-2">
            <QRCodeSVG value={urlVerificacion} size={52} level="M" bgColor="#ffffff" fgColor="#000000" />
            <div className="text-left text-[8px] leading-tight">
              <p className="font-bold text-[9px]">FIRMADO ELECTRÓNICAMENTE</p>
              <p>{firma?.firmante || data.medico}</p>
              <p className="text-gray-600">Cód. {r.codigo_verificacion.slice(0, 12)}</p>
              <p className="text-gray-600">Verificar: {urlVerificacion.replace(/^https?:\/\//, '')}</p>
            </div>
          </div>
        )}
    />
  );

  return (
    <div className="grid grid-cols-2 text-black">
      <div className="pr-3">
        {renderHeader()}
        {renderSeccion('ALERGIAS:', <p>{alergiasTexto}</p>)}
        {renderSeccion('PRESCRIPCIÓN:', renderPrescripcion())}
        {renderSeccion('DIAGNÓSTICO:', <p>{dxTexto}</p>)}
        {renderFirma()}
      </div>
      <div className="pl-3">
        {renderHeader()}
        {renderSeccion('SIGNOS DE ALARMA:', <p>{signosTexto}</p>)}
        {renderSeccion('INDICACIONES:', renderIndicaciones())}
        {renderSeccion('RECOMENDACIONES NO FARMACOLÓGICAS:', <p>{recomendTexto}</p>)}
        {renderSeccion('Próxima cita:', <p>{proximaTexto}</p>)}
        {renderFirma()}
      </div>
    </div>
  );
}

export function RecetaPrint({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isFetching } = useQuery<RecetaDetalle>({ queryKey: ['med-receta', id], queryFn: () => medRpc<RecetaDetalle>('med_receta', { _id: id }) });
  const { data: firma } = useQuery<FirmaEstado | null>({
    queryKey: ['med-receta-firma', id],
    queryFn: () => medRpc<FirmaEstado | null>('med_receta_firma_estado', { _id: id }),
    staleTime: 0,
    // mientras la firma está en cola, sondea hasta que el notificador la complete
    refetchInterval: (q) => (['pendiente', 'generando'].includes(q.state.data?.estado ?? '') ? 4000 : false),
  });
  const { user } = useAuth();
  const logo = user?.empresa?.logo;
  // Firma en el impreso: default por médico (medico_firma_modo), con override al imprimir.
  // electrónica = QR de verificación (.p12) · manual = imagen de la rúbrica.
  const [firmaModo, setFirmaModo] = useState<'electronica' | 'manual' | null>(null);
  const modo = firmaModo ?? (data?.medico_firma_modo === 'manual' ? 'manual' : 'electronica');
  const firmaManual = modo === 'manual';

  const firmar = useMutation({
    mutationFn: () => medRpc('med_receta_firmar', { _receta_id: id }),
    onSuccess: () => { toast.success('Firma encolada — el PDF firmado estará listo en segundos'); void qc.invalidateQueries({ queryKey: ['med-receta-firma', id] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const descargarPdf = async () => {
    try {
      const r = await medRpc<{ codigo: string; pdf: string } | null>('med_receta_pdf', { _id: id });
      if (!r?.pdf) { toast.error('El PDF firmado aún no está disponible'); return; }
      const a = document.createElement('a');
      a.href = `data:application/pdf;base64,${r.pdf}`;
      a.download = `${r.codigo || 'receta'}-firmada.pdf`;
      a.click();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <Modal title="Receta" onClose={onClose} wide>
      <style>{`@media print { @page { size: A4 landscape; margin: 8mm; } body * { visibility: hidden !important; } #receta-print, #receta-print * { visibility: visible !important; } #receta-print { position: absolute; left: 0; top: 0; width: 100%; padding: 0; } .no-print { display: none !important; } }`}</style>
      {isFetching || !data ? <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div> : (
        <>
          <div id="receta-print" className="bg-white text-black">
            <CopiaReceta data={data} logo={logo} firmaManual={firmaManual} firma={firma} />
          </div>
          <div className="no-print flex items-center gap-3 pt-3 border-t mt-3 flex-wrap">
            {!firmaManual && firma && (
              firma.estado === 'firmada'
                ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">PDF firmado ✓{firma.firmante ? ` · ${firma.firmante}` : ''}</span>
                : firma.estado === 'pendiente' || firma.estado === 'generando'
                  ? <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">firmando…</span>
                  : firma.estado === 'error'
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" title={firma.error ?? ''}>error de firma</span>
                    : firma.puede_firmar
                      ? <span className="text-xs text-muted-foreground">sin PDF firmado</span>
                      : <span className="text-xs text-amber-600" title="Configura el .p12 en la ficha del médico">⚠ médico sin .p12</span>
            )}
            {!firmaManual && firma?.puede_firmar && (firma.estado === null || firma.estado === 'error') && (
              <Button size="sm" variant="outline" isLoading={firmar.isPending} onClick={() => firmar.mutate()}>
                <FileSignature className="w-4 h-4 mr-1" /> Firmar (.p12)
              </Button>
            )}
            {firma?.tiene_pdf && (
              <Button size="sm" variant="outline" onClick={() => void descargarPdf()}>
                <Download className="w-4 h-4 mr-1" /> PDF firmado
              </Button>
            )}
            <div className="ml-auto flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                Firma:
                <select value={modo} onChange={(e) => setFirmaModo(e.target.value as 'electronica' | 'manual')} className="rounded border px-2 py-1 text-sm">
                  <option value="electronica">Electrónica (QR)</option>
                  <option value="manual">Manual (rúbrica / a mano)</option>
                </select>
              </label>
              <Button variant="outline" onClick={onClose}>Cerrar</Button>
              <Button onClick={() => window.print()}><Printer className="w-4 h-4 mr-1" /> Imprimir</Button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
